import { fabric } from "fabric";
import { jsPDF as JsPDF } from "jspdf";
import normalizeUrl from "normalize-url";

import { getNumberOfPages } from "../../canvas/page-panning";
import { pixelsToPoints } from "../paper.utils";
import { CanvasPaperConfig } from "./PageDesigner";

/**
 * Creates a PDF document from the given fabric canvas and configuration.
 *
 * @param canvas - The fabric canvas to be converted to a PDF. It contains various objects
 *                 which may include text, images, and shapes.
 * @param canvasPaperConfig - Configuration object containing paper size and orientation.
 *                            This defines the dimensions and layout of the PDF pages.
 * @param callback - Function to be called after the PDF document is created. This allows
 *                   further processing of the PDF document, such as saving or downloading it.
 */
export const createPdfDocument = async (
  canvas: fabric.Canvas,
  canvasPaperConfig: CanvasPaperConfig,
  callback: (doc: JsPDF) => void,
): Promise<void> => {
  const pageWidthInPoints = pixelsToPoints(canvasPaperConfig.width);
  const pageHeightInPoints = pixelsToPoints(canvasPaperConfig.height);

  const svgElement = canvasToSVG(canvas, canvasPaperConfig);

  const pdf = new JsPDF({
    orientation: canvasPaperConfig.orientation,
    unit: "pt",
    format: [pageWidthInPoints, pageHeightInPoints],
  });

  addStaticLinksToPdf(pdf, canvas);

  if (svgElement) {
    await addFontsToPDF(pdf, svgElement);
    await svgToPDF(pdf, svgElement, pageWidthInPoints, pageHeightInPoints);
  }

  callback(pdf);
};

/**
 * Adds static links to the PDF document if they exist in the canvas objects.
 *
 * @param doc - The JsPDF document to which links will be added. This document will contain
 *              clickable areas that redirect to specified URLs when viewed or printed.
 * @param canvas - The fabric canvas containing objects with potential URLs. Each object is
 *                 checked for a URL, and if found, the link is created accordingly.
 */
const addStaticLinksToPdf = (doc: JsPDF, canvas: fabric.Canvas): void => {
  for (const obj of canvas._objects) {
    const url = obj.data?.url;
    if (!url) {
      continue;
    }

    let { left = 0, top = 0, width = 0, height = 0 } = obj;
    if (obj instanceof fabric.Text) {
      const [charBounds] = obj.__charBounds ?? [[]];
      if (charBounds.length) {
        width = charBounds.reduce((sum, bounds) => sum + bounds.width, 0);
      }
    }

    doc.link(
      pixelsToPoints(left),
      pixelsToPoints(top),
      pixelsToPoints(width),
      pixelsToPoints(height),
      {
        url: normalizeUrl(url),
      },
    );
  }
};

/**
 * Adds fonts used in the SVG to the PDF document.
 *
 * @param doc - The JsPDF document where fonts will be added. This document requires fonts
 *              to render text correctly in the PDF output.
 * @param svgElement - The SVG element from which font faces will be extracted. The function
 *                     checks for all used font families and styles to ensure they are added
 *                     to the PDF document.
 */
export const addFontsToPDF = async (
  doc: JsPDF,
  svgElement: SVGElement,
): Promise<void> => {
  await Promise.allSettled(
    getListOfUsedFontFaces(svgElement).map(
      async ([fontFamily, fontStyle, fontWeight]) => {
        const fontId = `${fontFamily}_${fontStyle}_${fontWeight}`.replace(
          /\s/g,
          "",
        );
        await configureUnicodeFontIfAvailable(doc, fontId);
        doc.addFont(fontId, fontFamily, fontStyle, fontWeight);
      },
    ),
  );
};

/**
 * Converts the fabric canvas to an SVG element.
 *
 * @param canvas - The fabric canvas to be converted. It contains various objects that will
 *                 be represented in the SVG format.
 * @param canvasPaperConfig - Configuration object containing canvas dimensions. This is used
 *                            to set the viewBox of the generated SVG.
 * @returns An SVGElement created from the canvas or null if conversion fails.
 */
const canvasToSVG = (
  canvas: fabric.Canvas,
  canvasPaperConfig: CanvasPaperConfig,
): SVGElement | null => {
  const totalHeight =
    canvasPaperConfig.height * getNumberOfPages(canvas, canvasPaperConfig);

  const svg = canvas.toSVG({
    suppressPreamble: true,
    // set svg element size to avoid a blank page
    width: canvasPaperConfig.width,
    height: totalHeight,
    viewBox: {
      x: 0,
      y: 0,
      width: canvasPaperConfig.width,
      height: totalHeight,
    },
  });

  const parser = new DOMParser();
  return parser.parseFromString(svg, "image/svg+xml")
    .firstElementChild as SVGElement;
};

/**
 * Renders the given SVG element to the PDF document.
 *
 * @param doc - The JsPDF document to render the SVG into. This document will contain the
 *              visual representation of the SVG element once rendering is complete.
 * @param svgElement - The SVG element to be rendered. It should represent the full content
 *                     of the canvas in SVG format.
 * @param pageWidthInPoints - The width of the page in points. This is used to set the width
 *                            of the rendered SVG in the PDF.
 * @param pageHeightInPoints - The height of the page in points. This is used to determine
 *                             if the SVG content exceeds the page size, necessitating more pages.
 */
const svgToPDF = async (
  doc: JsPDF,
  svgElement: SVGElement,
  pageWidthInPoints: number,
  pageHeightInPoints: number,
): Promise<void> => {
  const svgHeight = Number(svgElement?.getAttribute("height"));
  const svgHeightInPoints = pixelsToPoints(svgHeight);

  await doc.svg(svgElement, {
    x: 0,
    y: 0,
    width: pageWidthInPoints,
    height: svgHeightInPoints,
  });

  if (svgHeightInPoints > pageHeightInPoints) {
    const pages = Math.ceil(svgHeightInPoints / pageHeightInPoints);
    for (let i = 0; i < pages - 1; i++) {
      doc.addPage([pageWidthInPoints, pageHeightInPoints]);
      await doc.svg(svgElement, {
        x: 0,
        y: -pageHeightInPoints * (i + 1),
        width: pageWidthInPoints,
        height: svgHeightInPoints,
      });
    }
  }
};

// Asynchronously injects the .ttf font file content into the JsPDF configuration so items
// using them can be rendered properly during print.
// Note that only a small subset of fonts was added here, and it will be increased as more
// users complain about the issue (per case basis).
//
// In order to add more fonts, find the .ttf file for them, with the correct style and
// weight, then put it here: https://rawgit.com/MrRio/jsPDF/master/fontconverter/fontconverter.html
// Then bring the generated file to the /fonts/ folder and format it to use like the rest.
const configureUnicodeFontIfAvailable = async (
  doc: JsPDF,
  fontId: string,
): Promise<void> => {
  let fontFile = null;
  switch (fontId) {
    // AdobeHandwriting-Ernie (Signature field text font-family)
    case "AdobeHandwriting-Ernie_normal_normal":
      fontFile = (await import("../../fonts/AdobeHandwriting-Ernie"))
        .signatureFieldTypeFontContent;
      break;

    // Arial
    case "Arial_normal_normal":
      fontFile = (await import("../../fonts/Arial-normal-normal")).fontContent;
      break;
    case "Arial_normal_bold":
      fontFile = (await import("../../fonts/Arial-normal-bold")).fontContent;
      break;

    // Arial Black
    case "ArialBlack_normal_normal":
      fontFile = (await import("../../fonts/ArialBlack-normal-normal"))
        .fontContent;
      break;
    case "ArialBlack_normal_bold":
      fontFile = (await import("../../fonts/ArialBlack-normal-bold"))
        .fontContent;
      break;

    // Arial Unicode MS
    case "ArialUnicodeMS_normal_normal":
      fontFile = (await import("../../fonts/ArialUnicodeMS-normal-normal"))
        .fontContent;
      break;
    case "ArialUnicodeMS_normal_bold":
      fontFile = (await import("../../fonts/ArialUnicodeMS-normal-bold"))
        .fontContent;
      break;

    // Avenir
    case "Avenir_normal_normal":
      fontFile = (await import("../../fonts/Avenir-normal-normal")).fontContent;
      break;
    case "Avenir_normal_bold":
      fontFile = (await import("../../fonts/Avenir-normal-bold")).fontContent;
      break;

    // Arial Rounded MT Bold
    case "ArialRoundedMTBold_normal_normal":
      fontFile = (await import("../../fonts/ArialRoundedMTBold-bold"))
        .fontContent;
      break;
    case "ArialRoundedMTBold_normal_bold":
      fontFile = (await import("../../fonts/ArialRoundedMTBold-bold"))
        .fontContent;
      break;

    // American Typewriter
    case "AmericanTypewriter_normal_normal":
      fontFile = (await import("../../fonts/American-Typewriter-normal-normal"))
        .fontContent;
      break;
    case "AmericanTypewriter_normal_bold":
      fontFile = (await import("../../fonts/American-Typewriter-normal-normal"))
        .fontContent;
      break;

    // Copperplate
    case "Copperplate_normal_normal":
      fontFile = (await import("../../fonts/Copperplate-normal")).fontContent;
      break;
    case "Copperplate_normal_bold":
      fontFile = (await import("../../fonts/Copperplate-normal")).fontContent;
      break;

    // Andale Mono
    case "AndaleMono_normal_normal":
      fontFile = (await import("../../fonts/Andale-Mono-normal-normal"))
        .fontContent;
      break;
    case "AndaleMono_normal_bold":
      fontFile = (await import("../../fonts/Andale-Mono-normal-normal"))
        .fontContent;
      break;

    // Arial Narrow
    case "ArialNarrow_normal_normal":
      fontFile = (await import("../../fonts/Arial-Narrow-normal")).fontContent;
      break;
    case "ArialNarrow_normal_bold":
      fontFile = (await import("../../fonts/Arial-Narrow-normal")).fontContent;
      break;

    // Comic Sans MS
    case "ComicSansMS_normal_normal":
      fontFile = (await import("../../fonts/Comic-Sans-MS-normal")).fontContent;
      break;
    case "ComicSansMS_normal_bold":
      fontFile = (await import("../../fonts/Comic-Sans-MS-normal")).fontContent;
      break;

    // DIN Alternate
    case "DINAlternate_normal_normal":
      fontFile = (await import("../../fonts/DIN-Alternate-Bold-normal"))
        .fontContent;
      break;
    case "DINAlternate_normal_bold":
      fontFile = (await import("../../fonts/DIN-Alternate-Bold-normal"))
        .fontContent;
      break;

    // DIN Condensed
    case "DINCondensed_normal_normal":
      fontFile = (await import("../../fonts/DIN-Condensed-Bold-normal"))
        .fontContent;
      break;
    case "DINCondensed_normal_bold":
      fontFile = (await import("../../fonts/DIN-Condensed-Bold-normal"))
        .fontContent;
      break;

    // Georgia
    case "Georgia_normal_normal":
      fontFile = (await import("../../fonts/Georgia-normal")).fontContent;
      break;
    case "Georgia_normal_bold":
      fontFile = (await import("../../fonts/Georgia-normal")).fontContent;
      break;

    // Zapfino
    case "Zapfino_normal_normal":
      fontFile = (await import("../../fonts/Zapfino-normal")).fontContent;
      break;
    case "Zapfino_normal_bold":
      fontFile = (await import("../../fonts/Zapfino-normal")).fontContent;
      break;

    // Impact
    case "Impact_normal_normal":
      fontFile = (await import("../../fonts/Impact-normal")).fontContent;
      break;
    case "Impact_normal_bold":
      fontFile = (await import("../../fonts/Impact-normal")).fontContent;
      break;

    // Avenir Next
    case "AvenirNext_normal_normal":
      fontFile = (await import("../../fonts/Avenir-Next-Regular-normal"))
        .fontContent;
      break;
    case "AvenirNext_normal_bold":
      fontFile = (await import("../../fonts/Avenir-Next-Regular-normal"))
        .fontContent;
      break;

    // Didot
    case "Didot_normal_normal":
      fontFile = (await import("../../fonts/Didot-normal")).fontContent;
      break;
    case "Didot_normal_bold":
      fontFile = (await import("../../fonts/Didot-normal")).fontContent;
      break;

    // Futura
    case "Futura_normal_normal":
      fontFile = (await import("../../fonts/Futura-Medium-normal")).fontContent;
      break;
    case "Futura_normal_bold":
      fontFile = (await import("../../fonts/Futura-Bold-normal")).fontContent;
      break;

    // Helvetica
    case "Helvetica_normal_normal":
      fontFile = (await import("../../fonts/Helvetica-normal")).fontContent;
      break;
    case "Helvetica_normal_bold":
      fontFile = (await import("../../fonts/Helvetica-Bold-normal"))
        .fontContent;
      break;

    // Helvetica Neue
    case "HelveticaNeue_normal_normal":
      fontFile = (await import("../../fonts/Helvetica-Neue-normal"))
        .fontContent;
      break;
    case "HelveticaNeue_normal_bold":
      fontFile = (await import("../../fonts/Helvetica-Neue-Bold-normal"))
        .fontContent;
      break;

    // Phosphate
    case "Phosphate_normal_normal":
      fontFile = (await import("../../fonts/Phosphate-Inline-normal"))
        .fontContent;
      break;
    case "Phosphate_normal_bold":
      fontFile = (await import("../../fonts/Phosphate-Inline-normal"))
        .fontContent;
      break;

    // Rockwell
    case "Rockwell_normal_normal":
      fontFile = (await import("../../fonts/Rockwell-Regular-normal"))
        .fontContent;
      break;
    case "Rockwell_normal_bold":
      fontFile = (await import("../../fonts/Rockwell-Bold-normal")).fontContent;
      break;

    // Avenir Next Condensed
    case "AvenirNextCondensed_normal_normal":
      fontFile = (
        await import("../../fonts/AvenirNextCondensed-Regular-normal")
      ).fontContent;
      break;
    case "AvenirNextCondensed_normal_bold":
      fontFile = (
        await import("../../fonts/AvenirNextCondensed-Regular-normal")
      ).fontContent;
      break;

    // Baskeville
    case "Baskerville_normal_normal":
      fontFile = (await import("../../fonts/Baskerville-normal")).fontContent;
      break;
    case "Baskerville_normal_bold":
      fontFile = (await import("../../fonts/Baskerville-normal")).fontContent;
      break;

    // BigCaslon
    case "BigCaslon_normal_normal":
      fontFile = (await import("../../fonts/BigCaslon-normal")).fontContent;
      break;
    case "BigCaslon_normal_bold":
      fontFile = (await import("../../fonts/BigCaslon-normal")).fontContent;
      break;

    // Bodoni 72 Smallcaps
    case "Bodoni72Smallcaps_normal_normal":
      fontFile = (await import("../../fonts/Bodoni-72-Smallcaps-Book-normal"))
        .fontContent;
      break;
    case "Bodoni72Smallcaps_normal_bold":
      fontFile = (await import("../../fonts/Bodoni-72-Smallcaps-Book-normal"))
        .fontContent;
      break;

    // Bodoni 72 Oldstyle
    case "Bodoni72Oldstyle_normal_normal":
      fontFile = (await import("../../fonts/BodoniSvtyTwoOSITCTT-Book-normal"))
        .fontContent;
      break;
    case "Bodoni72Oldstyle_normal_bold":
      fontFile = (await import("../../fonts/BodoniSvtyTwoOSITCTT-Book-normal"))
        .fontContent;
      break;

    // Bodoni 72
    case "Bodoni72_normal_normal":
      fontFile = (await import("../../fonts/BodoniSvtyTwoITCTT-Book-normal"))
        .fontContent;
      break;
    case "Bodoni72_normal_bold":
      fontFile = (await import("../../fonts/BodoniSvtyTwoITCTT-Book-normal"))
        .fontContent;
      break;

    // Bradley Hand
    case "BradleyHand_normal_normal":
      fontFile = (await import("../../fonts/Bradley-Hand-Bold-normal"))
        .fontContent;
      break;
    case "BradleyHand_normal_bold":
      fontFile = (await import("../../fonts/Bradley-Hand-Bold-normal"))
        .fontContent;
      break;

    // Brush Script
    case "BrushScriptMT_normal_normal":
      fontFile = (await import("../../fonts/Brush-Script-normal")).fontContent;
      break;
    case "BrushScriptMT_normal_bold":
      fontFile = (await import("../../fonts/Brush-Script-normal")).fontContent;
      break;

    // Chalkboard
    case "Chalkboard_normal_normal":
      fontFile = (await import("../../fonts/Chalkboard-normal")).fontContent;
      break;
    case "Chalkboard_normal_bold":
      fontFile = (await import("../../fonts/Chalkboard-normal")).fontContent;
      break;

    // Chalkboard SE
    case "ChalkboardSE_normal_normal":
      fontFile = (await import("../../fonts/ChalkboardSE-normal")).fontContent;
      break;
    case "ChalkboardSE_normal_bold":
      fontFile = (await import("../../fonts/ChalkboardSE-normal")).fontContent;
      break;

    // Geneva
    case "Geneva_normal_normal":
      fontFile = (await import("../../fonts/Geneva-normal")).fontContent;
      break;
    case "Geneva_normal_bold":
      fontFile = (await import("../../fonts/Geneva-normal")).fontContent;
      break;

    // Gill Sans
    case "GillSans_normal_normal":
      fontFile = (await import("../../fonts/GillSans-normal")).fontContent;
      break;
    case "GillSans_normal_bold":
      fontFile = (await import("../../fonts/GillSans-Bold-bold")).fontContent;
      break;

    // Herculanum
    case "Herculanum_normal_normal":
      fontFile = (await import("../../fonts/Herculanum-normal")).fontContent;
      break;
    case "Herculanum_normal_bold":
      fontFile = (await import("../../fonts/Herculanum-normal")).fontContent;
      break;

    // Lucida Grande
    case "LucidaGrande_normal_normal":
      fontFile = (await import("../../fonts/Lucida-Grande-normal")).fontContent;
      break;
    case "LucidaGrande_normal_bold":
      fontFile = (await import("../../fonts/Lucida-Grande-normal")).fontContent;
      break;

    // Luminari
    case "Luminari_normal_normal":
      fontFile = (await import("../../fonts/Luminari-normal")).fontContent;
      break;
    case "Luminari_normal_bold":
      fontFile = (await import("../../fonts/Luminari-normal")).fontContent;
      break;

    // Marker Felt
    case "MarkerFelt_normal_normal":
      fontFile = (await import("../../fonts/MarkerFelt-Thin-normal"))
        .fontContent;
      break;
    case "MarkerFelt_normal_bold":
      fontFile = (await import("../../fonts/MarkerFelt-Thin-normal"))
        .fontContent;
      break;

    // Microsoft Sans Serif
    case "MicrosoftSansSerif_normal_normal":
      fontFile = (await import("../../fonts/Microsoft-Sans-Serif-normal"))
        .fontContent;
      break;
    case "MicrosoftSansSerif_normal_bold":
      fontFile = (await import("../../fonts/Microsoft-Sans-Serif-normal"))
        .fontContent;
      break;

    // Noteworthy
    case "Noteworthy_normal_normal":
      fontFile = (await import("../../fonts/Noteworthy-Light-normal"))
        .fontContent;
      break;
    case "Noteworthy_normal_bold":
      fontFile = (await import("../../fonts/Noteworthy-Light-normal"))
        .fontContent;
      break;

    // SignPainter
    case "SignPainter_normal_normal":
      fontFile = (await import("../../fonts/SignPainter-normal")).fontContent;
      break;
    case "SignPainter_normal_bold":
      fontFile = (await import("../../fonts/SignPainter-normal")).fontContent;
      break;

    // Skia
    case "Skia_normal_normal":
      fontFile = (await import("../../fonts/Skia-normal")).fontContent;
      break;
    case "Skia_normal_bold":
      fontFile = (await import("../../fonts/Skia-normal")).fontContent;
      break;

    // Symbol
    case "Symbol_normal_normal":
      fontFile = (await import("../../fonts/Symbol-normal")).fontContent;
      break;
    case "Symbol_normal_bold":
      fontFile = (await import("../../fonts/Symbol-normal")).fontContent;
      break;

    // Times New Roman
    case "TimesNewRoman_normal_normal":
      fontFile = (await import("../../fonts/Times-New-Roman-normal"))
        .fontContent;
      break;
    case "TimesNewRoman_normal_bold":
      fontFile = (await import("../../fonts/Times-New-Roman-Bold-bold"))
        .fontContent;
      break;

    // Trebuchet MS
    case "TrebuchetMS_normal_normal":
      fontFile = (await import("../../fonts/Trebuchet-MS-normal")).fontContent;
      break;
    case "TrebuchetMS_normal_bold":
      fontFile = (await import("../../fonts/Trebuchet-MS-Bold-bold"))
        .fontContent;
      break;

    // Cochin
    case "Cochin_normal_normal":
      fontFile = (await import("../../fonts/Cochin-normal")).fontContent;
      break;
    case "Cochin_normal_bold":
      fontFile = (await import("../../fonts/Cochin-normal")).fontContent;
      break;

    // Curier New
    case "CourierNew_normal_normal":
      fontFile = (await import("../../fonts/Courier-New-normal")).fontContent;
      break;
    case "CourierNew_normal_bold":
      fontFile = (await import("../../fonts/Courier-New-normal")).fontContent;
      break;

    // Menlo
    case "Menlo_normal_normal":
      fontFile = (await import("../../fonts/Menlo-Regular-normal")).fontContent;
      break;
    case "Menlo_normal_bold":
      fontFile = (await import("../../fonts/Menlo-Regular-normal")).fontContent;
      break;

    // Optima
    case "Optima_normal_normal":
      fontFile = (await import("../../fonts/Optima-Regular-normal"))
        .fontContent;
      break;
    case "Optima_normal_bold":
      fontFile = (await import("../../fonts/Optima-Regular-normal"))
        .fontContent;
      break;

    // Palatino
    case "Palatino_normal_normal":
      fontFile = (await import("../../fonts/Palatino-Roman-normal"))
        .fontContent;
      break;
    case "Palatino_normal_bold":
      fontFile = (await import("../../fonts/Palatino-Roman-normal"))
        .fontContent;
      break;

    // Papyrus
    case "Papyrus_normal_normal":
      fontFile = (await import("../../fonts/Papyrus-normal")).fontContent;
      break;
    case "Papyrus_normal_bold":
      fontFile = (await import("../../fonts/Papyrus-normal")).fontContent;
      break;

    // Savoye LET
    case "SavoyeLET_normal_normal":
      fontFile = (await import("../../fonts/SavoyeLetPlain-normal"))
        .fontContent;
      break;
    case "SavoyeLET_normal_bold":
      fontFile = (await import("../../fonts/SavoyeLetPlain-normal"))
        .fontContent;
      break;

    // Snell Roundhand
    case "SnellRoundhand_normal_normal":
      fontFile = (await import("../../fonts/SnellRoundhand-normal"))
        .fontContent;
      break;
    case "SnellRoundhand_normal_bold":
      fontFile = (await import("../../fonts/SnellRoundhand-normal"))
        .fontContent;
      break;

    // Tahoma
    case "Tahoma_normal_normal":
      fontFile = (await import("../../fonts/Tahoma-normal")).fontContent;
      break;
    case "Tahoma_normal_bold":
      fontFile = (await import("../../fonts/Tahoma-Bold-bold")).fontContent;
      break;

    // Times
    case "Times_normal_normal":
      fontFile = (await import("../../fonts/Times-Roman-normal")).fontContent;
      break;
    case "Times_normal_bold":
      fontFile = (await import("../../fonts/Times-Bold-bold")).fontContent;
      break;

    // Trattatello
    case "Trattatello_normal_normal":
      fontFile = (await import("../../fonts/Trattatello-normal")).fontContent;
      break;
    case "Trattatello_normal_bold":
      fontFile = (await import("../../fonts/Trattatello-normal")).fontContent;
      break;

    // Verdana
    case "Verdana_normal_normal":
      fontFile = (await import("../../fonts/Verdana-normal")).fontContent;
      break;
    case "Verdana_normal_bold":
      fontFile = (await import("../../fonts/Verdana-normal")).fontContent;
      break;

    // Bahnschrift
    case "Bahnschrift_normal_normal":
      fontFile = (await import("../../fonts/Bahnschrift-normal")).fontContent;
      break;
    case "Bahnschrift_normal_bold":
      fontFile = (await import("../../fonts/Bahnschrift-normal")).fontContent;
      break;

    // Calibri
    case "Calibri_normal_normal":
      fontFile = (await import("../../fonts/Calibri-normal")).fontContent;
      break;
    case "Calibri_normal_bold":
      fontFile = (await import("../../fonts/Calibri-normal")).fontContent;
      break;

    // Cambria
    case "Cambria_normal_normal":
      fontFile = (await import("../../fonts/Cambria-normal")).fontContent;
      break;
    case "Cambria_normal_bold":
      fontFile = (await import("../../fonts/Cambria-normal")).fontContent;
      break;

    // Cambria Math
    case "CambriaMath_normal_normal":
      fontFile = (await import("../../fonts/Cambria-Math-normal")).fontContent;
      break;
    case "CambriaMath_normal_bold":
      fontFile = (await import("../../fonts/Cambria-Math-normal")).fontContent;
      break;

    // Candara
    case "Candara_normal_normal":
      fontFile = (await import("../../fonts/Candara-normal")).fontContent;
      break;
    case "Candara_normal_bold":
      fontFile = (await import("../../fonts/Candara-normal")).fontContent;
      break;

    // Chalkduster
    case "Chalkduster_normal_normal":
      fontFile = (await import("../../fonts/Chalkduster-normal")).fontContent;
      break;
    case "Chalkduster_normal_bold":
      fontFile = (await import("../../fonts/Chalkduster-normal")).fontContent;
      break;

    // Charter
    case "Charter_normal_normal":
      fontFile = (await import("../../fonts/Charter-Regular-normal"))
        .fontContent;
      break;
    case "Charter_normal_bold":
      fontFile = (await import("../../fonts/Charter-Regular-normal"))
        .fontContent;
      break;

    // Constantia
    case "Constantia_normal_normal":
      fontFile = (await import("../../fonts/Constantia-normal")).fontContent;
      break;
    case "Constantia_normal_bold":
      fontFile = (await import("../../fonts/Constantia-normal")).fontContent;
      break;

    // Corbel
    case "Corbel_normal_normal":
      fontFile = (await import("../../fonts/Corbel-normal")).fontContent;
      break;
    case "Corbel_normal_bold":
      fontFile = (await import("../../fonts/Corbel-normal")).fontContent;
      break;

    // Ebrima
    case "Ebrima_normal_normal":
      fontFile = (await import("../../fonts/Ebrima-normal")).fontContent;
      break;
    case "Ebrima_normal_bold":
      fontFile = (await import("../../fonts/Ebrima-normal")).fontContent;
      break;

    // Franklin Gothic Medium
    case "FranklinGothicMedium_normal_normal":
      fontFile = (await import("../../fonts/Franklin-Gothic-Medium-normal"))
        .fontContent;
      break;
    case "FranklinGothicMedium_normal_bold":
      fontFile = (await import("../../fonts/Franklin-Gothic-Medium-normal"))
        .fontContent;
      break;

    // Gabriola
    case "Gabriola_normal_normal":
      fontFile = (await import("../../fonts/Gabriola-normal")).fontContent;
      break;
    case "Gabriola_normal_bold":
      fontFile = (await import("../../fonts/Gabriola-normal")).fontContent;
      break;

    // Hoefler Text
    case "HoeflerText_normal_normal":
      fontFile = (await import("../../fonts/HoeflerText-Regular-normal"))
        .fontContent;
      break;
    case "HoeflerText_normal_bold":
      fontFile = (await import("../../fonts/HoeflerText-Regular-normal"))
        .fontContent;
      break;

    // Holo Lens MDL2 Assets
    case "HoloLensMDL2Assets_normal_normal":
      fontFile = (await import("../../fonts/HoloLens-MDL2-Assets-normal"))
        .fontContent;
      break;
    case "HoloLensMDL2Assets_normal_bold":
      fontFile = (await import("../../fonts/HoloLens-MDL2-Assets-normal"))
        .fontContent;
      break;

    // Ink Free
    case "InkFree_normal_normal":
      fontFile = (await import("../../fonts/Inkfree-normal")).fontContent;
      break;
    case "InkFree_normal_bold":
      fontFile = (await import("../../fonts/Inkfree-normal")).fontContent;
      break;

    // Lucida Sans Unicode
    case "LucidaSansUnicode_normal_normal":
      fontFile = (
        await import("../../fonts/Lucida-Sans-Unicode-Regular-normal")
      ).fontContent;
      break;
    case "LucidaSansUnicode_normal_bold":
      fontFile = (
        await import("../../fonts/Lucida-Sans-Unicode-Regular-normal")
      ).fontContent;
      break;

    // Microsoft Himalaya
    case "MicrosoftHimalaya_normal_normal":
      fontFile = (await import("../../fonts/Microsoft-Himalaya-normal"))
        .fontContent;
      break;
    case "MicrosoftHimalaya_normal_bold":
      fontFile = (await import("../../fonts/Microsoft-Himalaya-normal"))
        .fontContent;
      break;

    // Microsoft JhengHei
    case "MicrosoftJhengHei_normal_normal":
      fontFile = (await import("../../fonts/Microsoft-JhengHei-normal"))
        .fontContent;
      break;
    case "MicrosoftJhengHei_normal_bold":
      fontFile = (await import("../../fonts/Microsoft-JhengHei-normal"))
        .fontContent;
      break;

    // Microsoft New Tai Lue
    case "MicrosoftNewTaiLue_normal_normal":
      fontFile = (await import("../../fonts/Microsoft-New-Tai-Lue-normal"))
        .fontContent;
      break;
    case "MicrosoftNewTaiLue_normal_bold":
      fontFile = (await import("../../fonts/Microsoft-New-Tai-Lue-normal"))
        .fontContent;
      break;

    // Microsoft PhagsPa
    case "MicrosoftPhagsPa_normal_normal":
      fontFile = (await import("../../fonts/Microsoft-PhagsPa-normal"))
        .fontContent;
      break;
    case "MicrosoftPhagsPa_normal_bold":
      fontFile = (await import("../../fonts/Microsoft-PhagsPa-normal"))
        .fontContent;
      break;

    // Microsoft Tai Le
    case "MicrosoftTaiLe_normal_normal":
      fontFile = (await import("../../fonts/Microsoft-Tai-Le-normal"))
        .fontContent;
      break;
    case "MicrosoftTaiLe_normal_bold":
      fontFile = (await import("../../fonts/Microsoft-Tai-Le-normal"))
        .fontContent;
      break;

    // Microsoft YaHei
    case "MicrosoftYaHei_normal_normal":
      fontFile = (await import("../../fonts/Microsoft-Yahei-normal"))
        .fontContent;
      break;
    case "MicrosoftYaHei_normal_bold":
      fontFile = (await import("../../fonts/Microsoft-Yahei-normal"))
        .fontContent;
      break;

    // Microsoft Yi Baiti
    case "MicrosoftYiBaiti_normal_normal":
      fontFile = (await import("../../fonts/Microsoft-Yi-Baiti-normal"))
        .fontContent;
      break;
    case "MicrosoftYiBaiti_normal_bold":
      fontFile = (await import("../../fonts/Microsoft-Yi-Baiti-normal"))
        .fontContent;
      break;

    // MingLiU ExtB
    case "MingLiU-ExtB_normal_normal":
      fontFile = (await import("../../fonts/MingLiU-ExtB-normal")).fontContent;
      break;
    case "MingLiU-ExtB_normal_bold":
      fontFile = (await import("../../fonts/MingLiU-ExtB-normal")).fontContent;
      break;

    // Monaco
    case "Monaco_normal_normal":
      fontFile = (await import("../../fonts/Monaco-normal")).fontContent;
      break;
    case "Monaco_normal_bold":
      fontFile = (await import("../../fonts/Monaco-normal")).fontContent;
      break;

    // Nirmala UI
    case "NirmalaUI_normal_normal":
      fontFile = (await import("../../fonts/Nirmala-normal")).fontContent;
      break;
    case "NirmalaUI_normal_bold":
      fontFile = (await import("../../fonts/Nirmala-normal")).fontContent;
      break;

    // Palatino Linotype
    case "PalatinoLinotype_normal_normal":
      fontFile = (await import("../../fonts/Palatino-Linotype-normal"))
        .fontContent;
      break;
    case "PalatinoLinotype_normal_bold":
      fontFile = (await import("../../fonts/Palatino-Linotype-normal"))
        .fontContent;
      break;

    // Segoe MDL2 Assets
    case "SegoeMDL2Assets_normal_normal":
      fontFile = (await import("../../fonts/Segoe-MDL2-Assets-normal"))
        .fontContent;
      break;
    case "SegoeMDL2Assets_normal_bold":
      fontFile = (await import("../../fonts/Segoe-MDL2-Assets-normal"))
        .fontContent;
      break;

    // Segoe Print
    case "SegoePrint_normal_normal":
      fontFile = (await import("../../fonts/Segoe-Print-normal")).fontContent;
      break;
    case "SegoePrint_normal_bold":
      fontFile = (await import("../../fonts/Segoe-Print-normal")).fontContent;
      break;

    // Segoe Script
    case "SegoeScript_normal_normal":
      fontFile = (await import("../../fonts/Segoe-Script-normal")).fontContent;
      break;
    case "SegoeScript_normal_bold":
      fontFile = (await import("../../fonts/Segoe-Script-normal")).fontContent;
      break;

    // Segoe UI
    case "SegoeUI_normal_normal":
      fontFile = (await import("../../fonts/Segoe-UI-normal")).fontContent;
      break;
    case "SegoeUI_normal_bold":
      fontFile = (await import("../../fonts/Segoe-UI-bold-bold")).fontContent;
      break;

    // Sylfaen
    case "Sylfaen_normal_normal":
      fontFile = (await import("../../fonts/Sylfaen-Regular-normal"))
        .fontContent;
      break;
    case "Sylfaen_normal_bold":
      fontFile = (await import("../../fonts/Sylfaen-Regular-normal"))
        .fontContent;
      break;

    // Gadugi
    case "Gadugi_normal_normal":
      fontFile = (await import("../../fonts/Gadugi-normal")).fontContent;
      break;
    case "Gadugi_normal_bold":
      fontFile = (await import("../../fonts/Gadugi-normal")).fontContent;
      break;

    // Javanese Text
    case "JavaneseText_normal_normal":
      fontFile = (await import("../../fonts/Javanese-Text-normal")).fontContent;
      break;
    case "JavaneseText_normal_bold":
      fontFile = (await import("../../fonts/Javanese-Text-normal")).fontContent;
      break;

    // Leelawadee UI
    case "LeelawadeeUI_normal_normal":
      fontFile = (await import("../../fonts/Leelawadee-UI-normal")).fontContent;
      break;
    case "LeelawadeeUI_normal_bold":
      fontFile = (await import("../../fonts/Leelawadee-UI-normal")).fontContent;
      break;

    // Lucida Console
    case "LucidaConsole_normal_normal":
      fontFile = (await import("../../fonts/Lucida-Console-Regular-normal"))
        .fontContent;
      break;
    case "LucidaConsole_normal_bold":
      fontFile = (await import("../../fonts/Lucida-Console-Regular-normal"))
        .fontContent;
      break;

    // Malgun Gothic
    case "MalgunGothic_normal_normal":
      fontFile = (await import("../../fonts/Malgun-Gothic-normal")).fontContent;
      break;
    case "MalgunGothic_normal_bold":
      fontFile = (await import("../../fonts/Malgun-Gothic-normal")).fontContent;
      break;

    // Marlett
    case "Marlett_normal_normal":
      fontFile = (await import("../../fonts/Marlett-normal")).fontContent;
      break;
    case "Marlett_normal_bold":
      fontFile = (await import("../../fonts/Marlett-normal")).fontContent;
      break;

    // MV Boli
    case "MVBoli_normal_normal":
      fontFile = (await import("../../fonts/MV-Boli-normal")).fontContent;
      break;
    case "MVBoli_normal_bold":
      fontFile = (await import("../../fonts/MV-Boli-normal")).fontContent;
      break;

    // MS Gothic
    case "MSGothic_normal_normal":
      fontFile = (await import("../../fonts/MS-Gothic-normal")).fontContent;
      break;
    case "MSGothic_normal_bold":
      fontFile = (await import("../../fonts/MS-Gothic-normal")).fontContent;
      break;

    // SimSun
    case "SimSun_normal_normal":
      fontFile = (await import("../../fonts/SimSun-normal")).fontContent;
      break;
    case "SimSun_normal_bold":
      fontFile = (await import("../../fonts/SimSun-normal-bold")).fontContent;
      break;

    // Sitka
    case "Sitka_normal_normal":
      fontFile = (await import("../../fonts/Sitka-normal")).fontContent;
      break;
    case "Sitka_normal_bold":
      fontFile = (await import("../../fonts/Sitka-normal")).fontContent;
      break;

    // Yu Gothic
    case "YuGothic_normal_normal":
      fontFile = (await import("../../fonts/YuGothic-Regular-normal"))
        .fontContent;
      break;
    case "YuGothic_normal_bold":
      fontFile = (await import("../../fonts/YuGothic-Bold-bold")).fontContent;
      break;

    // Consolas
    case "Consolas_normal_normal":
      fontFile = (await import("../../fonts/Consolas-normal")).fontContent;
      break;
    case "Consolas_normal_bold":
      fontFile = (await import("../../fonts/Consolas-normal")).fontContent;
      break;

    // Mongolian Baiti
    case "MongolianBaiti_normal_normal":
      fontFile = (await import("../../fonts/Mongolian-Baiti-normal"))
        .fontContent;
      break;
    case "MongolianBaiti_normal_bold":
      fontFile = (await import("../../fonts/Mongolian-Baiti-normal"))
        .fontContent;
      break;

    // Myanmar Text
    case "MyanmarText_normal_normal":
      fontFile = (await import("../../fonts/Myanmar-Text-normal")).fontContent;
      break;
    case "MyanmarText_normal_bold":
      fontFile = (await import("../../fonts/Myanmar-Text-normal")).fontContent;
      break;

    default:
      fontFile = (await import("../../fonts/Arial-normal-normal")).fontContent;
      break;
  }

  if (fontFile) {
    doc.addFileToVFS(fontId, fontFile);
  }
};

/**
 * Retrieves a list of unique font faces used within a given SVG element.
 *
 * Each font face is represented as a tuple containing the font family,
 * font style, and font weight.
 *
 * @param element - The SVG element to inspect for font faces.
 * @returns An array of tuples, where each tuple contains the font family, style, and weight.
 */
const getListOfUsedFontFaces = (
  element: Element,
): Array<[string, string, string]> => {
  const fontFacesSet = new Set<string>();

  element?.querySelectorAll("*").forEach((e) => {
    const fontFamily = e.getAttribute("font-family");
    const fontStyle = e.getAttribute("font-style");
    const fontWeight = e.getAttribute("font-weight");

    if (fontFamily && fontStyle) {
      const fontFaceKey = fontWeight
        ? `${fontFamily}__${fontStyle}__${fontWeight}`
        : `${fontFamily}__${fontStyle}`;
      fontFacesSet.add(fontFaceKey);
    }
  });

  return [...fontFacesSet]
    .map<[string, string, string]>((fontFace: string) => {
      const [family, style, weight] = fontFace.split("__");
      return [family || "", style || "", weight || ""];
    })
    .filter(([family, style]) => family && style);
};
